import type { BoxProps } from '@withjoy/joykit';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { BulletPointFilled, StarFilled } from '@withjoy/joykit/icons';
import React, { useMemo } from 'react';

import { orderBy } from 'lodash-es';

import type { CustomerReviewStatsFragment } from '@graphql/generated';
import { DistributionChart, DistributionChartGroup } from './components/DistributionChart';
import type { DistributionChartEntry } from './StatsSummary.types';
import { pxToRem } from '@withjoy/joykit/theme';
import { SkeletonText } from '@shared/components/Skeleton';
import { useTranslation } from '@shared/core';

const numberFormatter = new Intl.NumberFormat('en-us');

type StatsSummaryProps = {
  stats: CustomerReviewStatsFragment;
} & BoxProps;

const Container: React.FC = ({ children }) => {
  return <Box marginTop={[7, null, pxToRem(56)]}>{children}</Box>;
};

const Header: React.FC = ({ children }) => {
  return (
    <Flex
      alignItems="center"
      columnGap={5}
    >
      {children}
    </Flex>
  );
};

export const StatsSummary = (props: StatsSummaryProps) => {
  const { stats } = props;
  const { t } = useTranslation('catalogRegistry');
  const { traitDistribution, ratingDistribution } = useMemo(() => {
    const ratingDistribution: DistributionChartEntry[] = orderBy(stats.distribution, dist => dist.ratingBucket, 'desc').map(dist => {
      return {
        id: dist.id,
        startLabel: `${dist.ratingBucket} stars`,
        endLabel: numberFormatter.format(dist.count),
        percentage: dist.percentage,
      };
    });

    const traitDistribution: DistributionChartEntry[] = stats.traits.map(({ id, label, averageRating }) => {
      return {
        id,
        startLabel: label,
        endLabel: averageRating.toFixed(1),
        percentage: averageRating / 5,
      };
    });

    return {
      ratingDistribution,
      traitDistribution,
    };
  }, [stats]);

  const formattedTotalReviewCount = useMemo(() => {
    return numberFormatter.format(stats.totalReviewCount);
  }, [stats.totalReviewCount]);
  return (
    <Container>
      <Header>
        <TextV2 typographyVariant={['hed6', null, 'display1']}>{stats.averageRating}</TextV2>
        <StarFilled />
        <BulletPointFilled size="sm" />
        <TextV2 typographyVariant={['hed6', null, 'display1']}>
          {t('catalogItemCustomerReviewsSection', 'statsSummary', 'totalReviewCount')({ reviewCount: formattedTotalReviewCount })}
        </TextV2>
      </Header>

      {traitDistribution.length || ratingDistribution.length ? (
        <DistributionChartGroup>
          <DistributionChart
            leadingColumnSizing="fixed"
            items={ratingDistribution}
          />
          <DistributionChart items={traitDistribution} />
        </DistributionChartGroup>
      ) : null}
    </Container>
  );
};

StatsSummary.Skeleton = () => {
  return (
    <Container>
      <Header>
        <SkeletonText
          rows={1}
          skeletonHeight={[31.2, null, 38]}
          width={[281, null, 326]}
        />
      </Header>
      <DistributionChartGroup>
        <DistributionChart.Skeleton />
      </DistributionChartGroup>
    </Container>
  );
};
